import QRCode from "react-qr-code";
import { Copy, Check, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";

const DATE_LOCALE_MAP = {
  en: "en-US",
  de: "de-DE",
  fr: "fr-FR",
  it: "it-IT",
  es: "es-ES",
  pt: "pt-PT",
  no: "nb-NO",
};

function formatExpiry(dateIso, lang) {
  return new Date(dateIso).toLocaleString(DATE_LOCALE_MAP[lang] || "en-US");
}

export default function ShareLinkBox({ id, shareLink, copied, onCopy, meta }) {
  const { t, lang } = useLang();
  return (
    <div className="border border-black/15 dark:border-white/15 bg-white dark:bg-black" data-testid="link-box">
      <div className="p-4 md:p-6 border-b border-black/10 dark:border-white/10 flex flex-col md:flex-row gap-3 items-stretch">
        <code
          data-testid="share-link"
          className="flex-1 font-mono text-xs md:text-sm text-neutral-800 dark:text-neutral-200 break-all bg-neutral-100 dark:bg-[#0d0d0d] p-4 border border-black/5 dark:border-white/5 select-all"
        >
          {shareLink}
        </code>
        <Button
          data-testid="copy-link-button"
          onClick={onCopy}
          className="rounded-none bg-black dark:bg-white text-white dark:text-black hover:bg-[#00ff66] font-bold uppercase tracking-widest text-xs h-auto px-6 md:min-w-[160px]"
        >
          <span className="inline-flex items-center gap-2">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? t("copied") : t("copy")}
          </span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2">
        <div
          className="p-6 md:p-10 border-b md:border-b-0 md:border-r border-black/10 dark:border-white/10 flex flex-col items-center justify-center bg-white"
          data-testid="qr-container"
        >
          <QRCode value={shareLink} size={180} level="M" bgColor="#ffffff" fgColor="#000000" />
          <span className="mt-4 text-xs font-mono uppercase tracking-widest text-black/60">
            {t("qr")}
          </span>
        </div>

        <div className="p-6 md:p-8 space-y-4 text-sm">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">ID</div>
            <code className="text-xs text-neutral-800 dark:text-neutral-200 font-mono break-all">{id}</code>
          </div>
          {meta && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">
                {t("expiresIn")}
              </div>
              <code className="text-xs text-neutral-800 dark:text-neutral-200 font-mono">
                {formatExpiry(meta.expires_at, lang)}
              </code>
            </div>
          )}
          <div className="flex gap-2 flex-wrap pt-2">
            {meta?.one_time && (
              <span className="inline-flex items-center gap-1 px-2 py-1 border border-[#ff3b30]/40 text-[#ff3b30] text-[10px] font-mono uppercase tracking-widest">
                <Flame className="w-3 h-3" /> burn-after-read
              </span>
            )}
            {meta?.has_password && (
              <span className="inline-flex items-center gap-1 px-2 py-1 border border-black/20 dark:border-white/20 text-neutral-700 dark:text-neutral-300 text-[10px] font-mono uppercase tracking-widest">
                password-locked
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
